// Actions
// Constants
import { TaskGroupingOption } from "@ms/uno-constants/lib/local/GroupingConstants";
import { StoreEntityState } from "@ms/uno-constants/lib/local/StoreConstants";
import { ViewRenderState } from "@ms/uno-constants/lib/local/ViewConstants";
import { getTaskFilterKey } from "@ms/planner-models/lib/local/utilities/FilterUtilities";
import { getSortCriteriaKey } from "@ms/planner-models/lib/local/utilities/SortCriteriaUtilities";
import { taskHoldsBulkUpdate } from "@ms/planner-models/lib/local/utilities/TaskUtilities";
import { Store } from "../stores/Store";
/**
 * Whether the key names no work to do, such as an empty list of task ids.
 * Without this guard an interaction with nothing to verify would report itself complete on the next
 * unrelated repaint, timing whatever that repaint was doing.
 * @param key What the interaction would be started with
 */ export function namesNoWork(key) {
    if (typeof key === "string") {
        return key.length === 0;
    }
    if (Array.isArray(key)) {
        return key.length === 0;
    }
    return typeof key === "object" && key !== null && "taskIds" in key && key.taskIds.length === 0;
}
/**
 * Every tracked interaction, keyed by name. Lives here rather than in telemetry because these checks
 * read stores, and telemetry sits below the store layer.
 *
 * Each entry narrows on `interactionId.name` before reading `key`.
 *
 * To add one: name it in `TrackedInteractionName`, declare its key in `IInteractionKeyOf`, then add an
 * entry here.
 */ export const TrackedInteractions = {
    /** Opening the task editor. Completed by the editor for that task rendering. */ OpenTaskEditor: {
        isComplete (interactionId, stores, targetViewId, renderState) {
            return renderState != null && renderState >= ViewRenderState.Critical;
        }
    },
    /** Opening My Day. Completed when the page reaches its critical render state. */ OpenMyDay: {
        isComplete (interactionId, stores, targetViewId, renderState) {
            return renderState != null && renderState >= ViewRenderState.Critical;
        }
    },
    /** Opening the Copilot pane. Completed by the pane rendering. */ OpenCatalystPane: {
        isComplete (interactionId, stores, targetViewId, renderState) {
            return renderState != null && renderState >= ViewRenderState.Critical;
        }
    },
    /** Opening the My Portfolios page. Completed by the page rendering. */ OpenMyPortfolios: {
        isComplete (interactionId, stores, targetViewId, renderState) {
            return renderState != null && renderState >= ViewRenderState.Critical;
        }
    },
    /** Opening a plan page. Completed by the plan page rendering, regardless of which view it lands on. */ OpenPlan: {
        isComplete (interactionId, stores, targetViewId, renderState) {
            return renderState != null && renderState >= ViewRenderState.Critical;
        }
    },
    /**
     * Opening the share plan dialog. Completed once the dialog has the plan, its container and the
     * container's members, which is when it can show who the plan is already shared with rather than
     * an empty people list.
     */ OpenSharePlanDialog: {
        isComplete (interactionId, stores, targetViewId, renderState) {
            return renderState != null && renderState >= ViewRenderState.Critical;
        }
    },
    /** Moving a Board task. Completed once the committed Board view shows it only in the destination bucket. */ TaskBucketIdUpdated: {
        isComplete (interactionId, stores) {
            if (interactionId.name !== "TaskBucketIdUpdated") {
                return false;
            }
            const taskStore = stores.load(Store.Tasks);
            const { taskId, sourceBucketId, destinationBucketId, boardViewId } = interactionId.key;
            const isInDestination = taskStore.getTasksForBoardViewGroup(boardViewId, TaskGroupingOption.Bucket, destinationBucketId).some((task)=>task.id === taskId);
            const isInSource = taskStore.getTasksForBoardViewGroup(boardViewId, TaskGroupingOption.Bucket, sourceBucketId).some((task)=>task.id === taskId);
            return isInDestination && !isInSource;
        }
    },
    /** Sorting the grid. Completed once the view holds the requested sort. */ GridSort: {
        isComplete (interactionId, stores, targetViewId) {
            if (interactionId.name !== "GridSort") {
                return false;
            }
            const applied = stores.load(Store.View).getSortCriteria(targetViewId);
            // Same object on the common path; keying is the fallback for a criteria rebuilt elsewhere.
            return applied === interactionId.key || getSortCriteriaKey(applied) === getSortCriteriaKey(interactionId.key);
        }
    },
    /** Filtering the grid from the Filter by menu. Completed once the view holds the requested filter. */ FilterByMenu: {
        isComplete (interactionId, stores, targetViewId) {
            if (interactionId.name !== "FilterByMenu") {
                return false;
            }
            const applied = stores.load(Store.View).getCurrentTaskFilter(targetViewId);
            // The view stores the very object the caller passed, so this hits on the common path and
            // keeps a dynamic filter's task-id list from being keyed on every repaint.
            return applied === interactionId.key || getTaskFilterKey(applied) === getTaskFilterKey(interactionId.key);
        }
    },
    /**
     * Pasting tasks. Completed once every pasted task is in the store, which is the optimistic write.
     * Ids are client-generated, so a server response arriving first drops the measurement.
     */ GridPasteTasks: {
        isComplete (interactionId, stores) {
            if (interactionId.name !== "GridPasteTasks") {
                return false;
            }
            return interactionId.key.every((taskId)=>stores.load(Store.Tasks).getTask(taskId).data != null);
        }
    },
    /** Deleting rows. Completed once every deleted task has left the grid. */ GridDeleteRows: {
        isComplete (interactionId, stores) {
            if (interactionId.name !== "GridDeleteRows") {
                return false;
            }
            // Deletes are optimistic and the entity is kept in the table so undo can recover it, so
            // its data outliving the delete proves nothing. Status is what the grid reads: it leaves
            // Present the moment the row is taken off screen, before the server has answered.
            return interactionId.key.every((taskId)=>{
                const { data, status } = stores.load(Store.Tasks).getTask(taskId);
                return data == null || status.state === StoreEntityState.SoftDeleted || status.state === StoreEntityState.Absent;
            });
        }
    },
    /**
     * Moving tasks by cut and paste. Checks position rather than presence, since a move creates
     * nothing and the tasks it names already exist.
     */ GridMoveTasks: {
        isComplete (interactionId, stores) {
            if (interactionId.name !== "GridMoveTasks") {
                return false;
            }
            return interactionId.key.every(({ taskId, orderHint })=>stores.load(Store.Tasks).getTask(taskId).data?.getPlanViewOrderHint(TaskGroupingOption.None) === orderHint);
        }
    },
    /** Updating a field across selected tasks. Completed once every task reads back the new value. */ GridBulkUpdate: {
        isComplete (interactionId, stores) {
            if (interactionId.name !== "GridBulkUpdate") {
                return false;
            }
            const { taskIds, field, value } = interactionId.key;
            return taskIds.every((taskId)=>taskHoldsBulkUpdate(stores.load(Store.Tasks).getTask(taskId).data, field, value));
        }
    }
};
